import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { apiClient } from '../../../shared/utils/apiClient';
import EmailSignInLink from '@/domains/auth/components/EmailSignInLink';

interface QuickApplyProps {
  job_id: number;
  apply_path: string;
  user_signed_in: boolean;
  authenticity_token: string;
  magic_link_enabled?: boolean;
  captcha_site_key?: string | null;
}

type ModalStep = 'email' | 'password';

interface QuickApplyResponse {
  error?: string;
  status?: string;
  auth?: {
    'access-token': string;
    client: string;
    uid: string;
  };
}

/**
 * QuickApply - Modal popup with email/password flow for job applications
 *
 * For logged-in users: Direct link to /jobs/:id/apply
 * For non-logged-in users: Modal with email lookup, then:
 *   - New users: Redirect to /jobs/:id/apply?email=xxx
 *   - Existing users: Show password field for inline auth
 */
export default function QuickApply({
  job_id,
  apply_path,
  user_signed_in,
  authenticity_token,
  magic_link_enabled = false,
  captcha_site_key = null,
}: QuickApplyProps) {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState<ModalStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLoggedInApply = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post<QuickApplyResponse>(`/api/v1/quick_apply/${job_id}/submit`, {}, {
        headers: { 'X-CSRF-Token': authenticity_token },
      });

      if (response.ok) {
        // applied=true shows the job page's post-apply prompts.
        window.location.href = `/jobs/${job_id}?applied=true`;
        return;
      }

      if (response.status === 409) {
        // Already applied: back to the job page as-is.
        window.location.href = `/jobs/${job_id}`;
        return;
      }

      if (response.status === 422) {
        // Profile incomplete — fall back to full form
        window.location.href = apply_path;
        return;
      }

      setError(response.data?.error || response.error || 'Failed to submit application');
    } catch {
      // Network error — fall back to full form
      window.location.href = apply_path;
    } finally {
      setIsSubmitting(false);
    }
  };

  // If logged in, submit directly via API
  if (user_signed_in) {
    return (
      <div data-testid="quick-apply">
        {error && <div className="text-danger small mb-1">{error}</div>}
        <button
          type="button"
          onClick={handleLoggedInApply}
          className="job-btn job-btn--apply job-btn--large job-btn--block quick-apply-widget"
          disabled={isSubmitting}
          data-testid="quick-apply-button"
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Applying...
            </>
          ) : (
            'Quick Apply'
          )}
        </button>
      </div>
    );
  }

  const handleOpenModal = () => {
    setShowModal(true);
    setStep('email');
    setEmail('');
    setPassword('');
    setError(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await apiClient.post<QuickApplyResponse>('/api/v1/quick_apply/lookup_email', { email, job_id }, {
        headers: { 'X-CSRF-Token': authenticity_token },
      });
      const result = response.data;

      if (!response.ok) {
        setError(result?.error || response.error || 'Something went wrong');
        return;
      }

      if (result.status === 'new') {
        // New user -> redirect to multi-step form with email pre-filled
        window.location.href = `/jobs/${job_id}/apply?email=${encodeURIComponent(email)}`;
      } else {
        // Existing user -> show password field
        setStep('password');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await apiClient.post<QuickApplyResponse>('/api/v1/quick_apply/inline_auth', { email, password }, {
        headers: { 'X-CSRF-Token': authenticity_token },
      });
      const result = response.data;

      if (!response.ok) {
        setError(result?.error || response.error || 'Invalid password');
        return;
      }

      if (!result.auth) {
        setError('Authentication failed. Please try again.');
        return;
      }

      // Store auth tokens (wrapped in try/catch for private browsing mode)
      try {
        localStorage.setItem('access-token', result.auth['access-token']);
        localStorage.setItem('client', result.auth.client);
        localStorage.setItem('uid', result.auth.uid);
      } catch {
        // localStorage unavailable (private browsing), continue anyway
      }

      // Submit application directly for existing users
      await submitApplication(result.auth);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const submitApplication = async (auth: {
    'access-token': string;
    client: string;
    uid: string;
  }) => {
    try {
      const response = await apiClient.post<QuickApplyResponse>(`/api/v1/quick_apply/${job_id}/submit`, { email }, {
        headers: {
          'X-CSRF-Token': authenticity_token,
          'access-token': auth['access-token'],
          client: auth.client,
          uid: auth.uid,
        },
      });

      if (response.ok) {
        // Show success or redirect
        window.location.href = `/jobs/${job_id}?applied=true`;
      } else {
        setError(response.data?.error || response.error || 'Failed to submit application');
      }
    } catch {
      setError('Failed to submit application. Please try again.');
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setError(null);
  };

  return (
    <div data-testid="quick-apply">
      <button
        type="button"
        onClick={handleOpenModal}
        className="job-btn job-btn--apply job-btn--large job-btn--block quick-apply-widget"
        data-testid="quick-apply-button"
      >
        Apply Now
      </button>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Quick Apply</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {step === 'email' && (
            <Form onSubmit={handleEmailSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="mb-0">Email Address</h5>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </Form.Group>
              <div className="d-grid">
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </Form>
          )}

          {step === 'password' && (
            <Form onSubmit={handlePasswordSubmit}>
              <p className="text-muted mb-3">
                Welcome back! Enter your password to continue.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={email} disabled />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </Form.Group>
              <div className="text-end mb-3">
                <a
                  href="/users/password/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small"
                >
                  Forgot password?
                </a>
              </div>
              <div className="d-grid gap-2">
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In & Apply'
                  )}
                </Button>
                <Button variant="link" onClick={handleBackToEmail} disabled={isLoading}>
                  Use different email
                </Button>
              </div>
              {magic_link_enabled ? (
                <div className="mt-3">
                  <p className="text-center text-muted small mb-2">Forgot your password? We can email you a sign-in link.</p>
                  <EmailSignInLink
                    email={email}
                    returnPath={apply_path}
                    csrfToken={authenticity_token}
                    captchaSiteKey={captcha_site_key}
                  />
                </div>
              ) : null}
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
